import { IsDevEnv, variantProvider } from '@/app'
import { LoadingScreen, View } from '@/components'
import { Learn } from '@/features'
import { APIClient, analytics } from '@/services'
import { Page } from '@/types'
import { useElementScroll, usePress } from '@/utils'
import { AnyFunction, useRef } from '@codeleap/common'
import { TypeGuards } from '@codeleap/types'
import React from 'react'

type LessonPageProps = {
  page: Page
  goToNextPage: AnyFunction
  goToPreviousPage: AnyFunction
  visible: boolean
  index?: number
  children?: React.ReactNode
  currentPage?: number
}

const DEBUG = IsDevEnv && false
const VIEWED_PREVIOUS_PAGES = 3
const VIEWED_NEXT_PAGES = 5

export const LessonPage = (props: LessonPageProps) => {
  const { page, goToNextPage, goToPreviousPage, visible, index, children, currentPage } = props

  const prevAnswers = useRef([])
  const { inSwipe } = Learn.useLessonContext()

  const questionId = page?.question?.id ?? (TypeGuards.isNumber(page?.question) ? page?.question : null)

  const question = APIClient.Learn.useQuizQuestion({ questionId })

  const selectedChoice = Learn.S.learn(state => state?.choices?.[question?.id])

  const hasQuestionAndNotSelectedChoice = !!question?.id && !!page?.question
  const hasChildren = React.isValidElement(children)

  const pressNext = usePress(() => {
    if (inSwipe.current && visible) return
    if (!hasQuestionAndNotSelectedChoice) {
      goToNextPage()
    }

    if (DEBUG) logger.log('goToNextPage', { hasQuestionAndNotSelectedChoice })
  })

  const pressPrevious = usePress(() => {
    if (inSwipe.current && visible) return
    goToPreviousPage()

    if (DEBUG) {
      logger.log('goToPreviousPage', { hasQuestionAndNotSelectedChoice })
    }
  })

  const scroll = useElementScroll()

  const onScroll = () => {
    pressNext.onInvalidatePress()
    pressPrevious.onInvalidatePress()
  }

  const PressableOverlay = ({ inContent = false, style = {} }) => {
    if ((hasQuestionAndNotSelectedChoice && inContent) || (hasChildren && inContent)) {
      return null
    }

    const zIndex = inContent ? 2 : 0

    return (
      <View css={[styles.pressOverlay, { zIndex }, !inContent && { top: scroll?.offsetY }]}>
        <View onClick={pressPrevious.onPress} css={[styles.pressableOverlayArea, DEBUG && { background: '#FF05' }]} />
        <View onClick={pressNext.onPress} css={[styles.pressableOverlayArea, DEBUG && { background: '#F005' }]} />
      </View>
    )
  }

  const onNext = () => {
    if (!selectedChoice?.is_correct && !prevAnswers.current.find(answer => answer === currentPage)) {
      analytics.track('lesson_error')
      prevAnswers.current = [...prevAnswers.current, currentPage]
    }
    Learn.LessonViewContent.QuizFeedback.toggle(question?.id)
    setTimeout(() => goToNextPage(), 450)
  }

  const adjacentPage = currentPage - VIEWED_PREVIOUS_PAGES >= index || currentPage + VIEWED_NEXT_PAGES <= index

  if ((!!page?.question && !question?.choices) || (!page?.question && adjacentPage && !visible)) {
    return <LoadingScreen transparent />
  }

  return (
    <>
      <div style={styles.content} onScroll={onScroll} ref={scroll.ref}>
        <Learn.LessonViewContent
          visible={visible}
          question={question}
          page={page}
          renderOverlay={<PressableOverlay />}
          hasChildren={hasChildren}
        >
          <Learn.LessonViewContent.Img page={page} question={question} style={styles.image} />

          <PressableOverlay inContent />

          <Learn.LessonViewContent.Quiz page={page} question={question} />

          {children}
        </Learn.LessonViewContent>

        <Learn.LessonViewContent.Video page={page} style={{ ...styles.pressableVideoOverlay, top: scroll?.offsetY }} />

        <Learn.LessonViewNext
          goToNext={goToNextPage}
          visible={hasQuestionAndNotSelectedChoice || hasChildren ? false : visible}
        />
      </div>

      <Learn.LessonViewContent.QuizFeedback question={question} onNext={onNext} />
    </>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    image: {
      zIndex: -1,
      width: '100%',
      height: 'calc(100svh - 120px)',
      backgroundColor: 'transparent',
      marginBottom: 24
    },
    content: {
      minHeight: '100svh',
      maxHeight: '100svh',
      overflowY: 'hidden',
      overflowX: 'hidden',
      position: 'relative',
      pointerEvents: 'all'
    },
    pressOverlay: {
      width: '100%',
      height: '100%',
      ...theme.presets.absolute,
      ...theme.presets.whole,
      backgroundColor: theme.colors.transparent,
      ...theme.presets.row,
      ...theme.presets.justifySpaceBetween
    },
    pressableOverlayArea: {
      width: '50%'
    },
    pressableVideoOverlay: {
      backgroundColor: DEBUG ? '#0f25' : theme.colors.transparent,
      width: '100vw',
      height: 'calc(100svh * 0.5)',
      ...theme.presets.absolute,
      right: 0,
      left: 0,
      top: 0,
      zIndex: 5
    }
  }),
  true
)
